import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_THEME = {
  id: 'ordinary',
  name: 'Vanlig tid',
  colors: {
    section1: '#FFFEF7',
    section2: '#FEF3C7',
    section3: '#FFFBEB',
    accent: '#D97706',
    accentSoft: '#FEF3C7',
    accentText: '#FFFFFF',
    textHeading: '#111827',
    textBody: '#374151',
    textMuted: '#6B7280',
    textHero: '#FFFBEB',
  },
};

const ThemeContext = createContext({
  theme: FALLBACK_THEME,
  reloadTheme: () => {},
});

// Push the active theme's colors as CSS custom properties on :root so any
// component can do bg-[var(--theme-section-1)], color: var(--theme-text-body), etc.
const applyThemeToRoot = (theme) => {
  if (typeof document === 'undefined' || !theme?.colors) return;
  const root = document.documentElement;
  const c = theme.colors;
  root.style.setProperty('--theme-section-1', c.section1);
  root.style.setProperty('--theme-section-2', c.section2);
  root.style.setProperty('--theme-section-3', c.section3);
  root.style.setProperty('--theme-accent', c.accent);
  root.style.setProperty('--theme-accent-soft', c.accentSoft);
  root.style.setProperty('--theme-accent-text', c.accentText);
  // Text colors — fall back to safe defaults if the active theme doc is old/incomplete
  root.style.setProperty('--theme-text-heading', c.textHeading || '#111827');
  root.style.setProperty('--theme-text-body', c.textBody || '#374151');
  root.style.setProperty('--theme-text-muted', c.textMuted || '#6B7280');
  root.style.setProperty('--theme-text-hero', c.textHero || '#FFFFFF');
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(FALLBACK_THEME);

  const loadTheme = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/theme/active`);
      if (res.data) {
        setTheme(res.data);
        applyThemeToRoot(res.data);
      }
    } catch (err) {
      // Silently fall back; the public site is still usable on the default tokens
      applyThemeToRoot(FALLBACK_THEME);
    }
  }, []);

  // Apply fallback synchronously so first paint already has tokens, then refresh from API
  useEffect(() => {
    applyThemeToRoot(FALLBACK_THEME);
    loadTheme();
  }, [loadTheme]);

  const value = useMemo(() => ({ theme, reloadTheme: loadTheme }), [theme, loadTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
